/**
 * The top level of an MP4 or QuickTime file, read from the headers alone
 * (1.174.0).
 *
 * **One walk for two readers.** `videoOrt.ts` looks for `moov` to read time
 * and place, `videowandeln.ts` to learn where every picture lies in the file.
 * Both used to need the same loop -- including the two traps below, which a
 * second copy would have to know as well.
 *
 * Only the headers are read (sixteen bytes per box, through `Blob.slice`):
 * the boxes themselves can be gigabytes, and the point of walking them is not
 * to hold them.
 */

/** A box at the top level: its four-letter type, where it starts, and how long it is. */
export interface TopBox {
  type: string
  /** Offset of the header in the file. */
  start: number
  /** Whole length, header included. */
  size: number
  /** Offset of the content, behind the header (8 or 16 bytes). */
  body: number
}

/**
 * A broken file must not walk forever: a camera file has a handful of boxes
 * at the top, a fragmented one a few hundred.
 */
const MOST_BOXES = 4096

/** Four characters from a header. */
function typeAt(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  )
}

/**
 * The boxes at the top level, as far as the headers add up.
 *
 * **What comes before a broken header still counts**: a file cut off after
 * its `moov` still has a readable `moov`. A reader that needs every box (the
 * converter, which must know there is no `moof` behind it) checks `complete`.
 * With `until`, the walk stops at the first box of that type -- reading time
 * and place needs nothing behind `moov`.
 *
 * **Length 1 is not a case to skip**: the real length then follows as a
 * 64-bit number. For the `mdat` of a long 4K video that is the normal case,
 * and whoever skips it misreads by gigabytes and never finds `moov`.
 * **Length 0 means "to the end of the file"**, so it is the last box.
 */
export async function topBoxes(file: Blob, until?: string): Promise<{ boxes: TopBox[]; complete: boolean }> {
  const boxes: TopBox[] = []
  let offset = 0
  while (offset + 8 <= file.size) {
    if (boxes.length >= MOST_BOXES) return { boxes, complete: false }
    const head = new DataView(await file.slice(offset, offset + 16).arrayBuffer())
    if (head.byteLength < 8) return { boxes, complete: false }
    let size = head.getUint32(0)
    const type = typeAt(head, 4)
    let body = offset + 8
    if (size === 1) {
      if (head.byteLength < 16) return { boxes, complete: false }
      // The upper half counts only when the file really goes past 4 GB;
      // `getBigUint64` is not equally forgiving everywhere.
      size = head.getUint32(8) * 2 ** 32 + head.getUint32(12)
      body = offset + 16
    } else if (size === 0) {
      size = file.size - offset
    }
    if (size < body - offset) return { boxes, complete: false }
    boxes.push({ type, start: offset, size, body })
    if (type === until) return { boxes, complete: false }
    offset += size
  }
  // A last box that claims more than the file holds is a cut-off file.
  return { boxes, complete: offset === file.size }
}
